<template>
  <div
    class="roomie-currency-input"
    @click="focus">
    <div
      v-if="label"
      class="roomie-input__label-container">
      <label
        class="roomie-input__label"
        :for="id">{{ label }}
        <span
          v-if="required"
          class="roomie-input__require-mark">
          *
        </span>
      </label>
      <slot name="after-label" />
    </div>
    <div
      :class="[
        `roomie-input__container ${status}`,
        disabled && 'disabled',
        readOnly && 'read-only'
      ]"
      :data-lh-id="inputTrackingLhId"
      @click="$emit('click')">
      <span class="roomie-currency-input__symbol">
        {{ symbol }}
      </span>

      <span
        v-if="disabled"
        class="disabled-placeholder">
        —
      </span>
      <div
        v-else
        class="roomie-currency-input__container">
        <input
          :id="id"
          ref="input"
          pattern="[\d.\,]*"
          inputmode="numeric"
          v-bind="$attrs"
          :value="formattedValue"
          :name="name"
          :autofocus="autofocus"
          :placeholder="placeholder"
          :disabled="disabled || readOnly"
          :class="[
            `roomie-input ${status}`,
            'roomie-currency-input__field',
            disabled && 'disabled'
          ]"
          @focus="$emit('focus')"
          @blur="$emit('blur')"
          @input="handleInput">
      </div>

      <span
        v-if="currency"
        class="roomie-currency-input__currency">
        {{ currency }}
      </span>
    </div>
    <span
      v-show="helperText"
      :class="['roomie-input__helper-text', !(disabled || readOnly) && status]">
      {{ helperText }}
    </span>
  </div>
</template>

<script>
import { AVAILABLE_LOCALES } from './../../utils/constants'
import { cleanOutput } from './utils'

export default {
  name: 'RoomieCurrencyInput',
  props: {
    id: {
      type: String,
      required: true,
    },
    label: {
      type: String,
      required: false,
      default: ''
    },
    value: {
      type: [String, Number],
      required: false,
      default: ''
    },
    modelValue: {
      type: [String, Number],
      required: false,
      default: '',
    },
    maxLength: {
      type: Number,
      required: false,
      default: 14
    },
    name: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: 'Ingresa un valor',
    },
    status: {
      type: String,
      required: false,
      default: '',
    },
    required: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    readOnly: {
      type: Boolean,
      required: false,
      default: false,
    },
    currency: {
      type: String,
      required: false,
      default: ''
    },
    locale: {
      type: String,
      default: 'es-CO',
      validator: value => AVAILABLE_LOCALES.includes(value)
    },
    symbol: {
      type: String,
      required: false,
      default: '$'
    },
    helperText: {
      type: String,
      default: ''
    },
    inputTrackingLhId:{
      type: String,
      required: false,
      default: null
    },
    autofocus: {
      type: Boolean,
      default: false
    },
    outputType: {
      type: String,
      default: 'number',
      validator: value => ['number', 'string'].includes(value)
    }
  },
  emits:['input', 'click', 'focus', 'blur', 'update:modelValue'],
  computed: {
    formattedValue () {
      return this.formatter.format(this.value || this.modelValue)
    },
    shouldEmitNumber () {
      return this.outputType === 'number'
    }
  },
  watch: {
    locale (newLocale) {
      this.formatter = new Intl.NumberFormat(newLocale)
    }
  },
  created () {
    this.formatter = new Intl.NumberFormat(this.locale)
  },
  methods: {
    emitValue (value) {
      let valueToEmit

      if (value != '') {
        valueToEmit = this.shouldEmitNumber ? parseInt(value) : value
      } else {
        valueToEmit = this.shouldEmitNumber ? 0 : ''
      }

      this.$emit('input', valueToEmit)
      this.$emit('update:modelValue', valueToEmit)
    },
    async handleInput (event) {
      const { target } = event

      const valueToEmit = cleanOutput(target.value)

      this.emitValue(valueToEmit)
      await this.$forceUpdate()
      await this.$nextTick()
    },
    focus() {
      if (this.$refs && this.$refs.input) {
        this.$refs.input.focus()
        this.selectContent()
      }
    },
    selectContent () {
      const valueLength = this.$refs.input.value.length
      this.$refs.input.setSelectionRange(0, valueLength)
    },
  }
}
</script>

<style
  src="./_index.scss"
  lang="scss"/>
